import { ethers } from "ethers-v5";
import { getConfig } from "../constants/config";
import tokenService from "../../common/tokenService";

/**
 * Calculates the total output amount from a route array.
 *
 * @param {Array} route - The route array from getRoute response
 *
 * @returns {Promise<Object>} An object containing:
 *   - amountOut {ethers.BigNumber|undefined}: Total output amount in raw units
 *   - amountOutFormatted {string|undefined}: Total output amount formatted with token decimals
 *   - tokenIn {string|undefined}: Input token address
 *   - tokenOut {string|undefined}: Output token address
 *   Returns object with all undefined values if route is invalid
 *
 * @example
 * // Example 1: Get total output
 * const route = await getRoute({
 *   tokenIn: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   tokenOut: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
 *   amount: "1000000000000000000" // 1 WSEI
 * });
 * const { amountOut, amountOutFormatted } = await getTotalAmountOut(route);
 * console.log(amountOutFormatted); // "1.5" USDC
 *
 * @note Note: For multi-path routes, this sums up amounts across all paths
 */
export const getTotalAmountOut = async (route) => {
  if (!route || !Array.isArray(route)) {
    console.error("Invalid or missing `route`: Must be an array.");
    return {
      amountOut: undefined,
      amountOutFormatted: undefined,
      tokenIn: undefined,
      tokenOut: undefined,
    };
  }

  // Get config for non-token data
  const config = getConfig();
  const { chainId, additionalTokens, overrideDefaultTokens, nativeAddress } =
    config;

  // Get updated token list with user's configuration applied
  const tokens = await tokenService.getAsyncTokenList(
    chainId,
    additionalTokens,
    overrideDefaultTokens,
    nativeAddress
  );

  const tokenIn = tokens[route[0][0].tokenIn];
  const tokenOut = tokens[route[0].at(-1).tokenOut];

  let totalAmountOut = ethers.BigNumber.from(0);

  for (let i = 0; i < route.length; i++) {
    totalAmountOut = totalAmountOut.add(
      ethers.BigNumber.from(route[i].at(-1)?.amountOutMin || 0)
    );
  }

  return {
    amountOut: totalAmountOut,
    amountOutFormatted: ethers.utils
      .formatUnits(totalAmountOut, tokenOut.attributes.decimals)
      .toString(),
    tokenIn: tokenIn.attributes.address,
    tokenOut: tokenOut.attributes.address,
  };
};
